import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Loader2, ShieldCheck, Mail, Smartphone, ArrowLeft, AlertCircle, Fingerprint } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { api, errMsg } from './api';
import { usePortal } from './PortalAuth';
import { inputCls, goldBtn } from './ui';
import { useLoginTheme } from './theme';
import { LOGO } from './shells/nav';
import './themes.css';
import { canUseBiometric, verifyBiometric } from './biometric';

function CodeInput({ value, onChange }) {
  return (
    <InputOTP maxLength={6} value={value} onChange={onChange} autoFocus data-testid="mfa-code-input">
      <InputOTPGroup>
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <InputOTPSlot key={i} index={i} className="w-12 h-14 text-xl font-mono border-[#1F242C] text-[#F2F2F2] bg-[#050505]" />
        ))}
      </InputOTPGroup>
    </InputOTP>
  );
}

export default function PortalLogin() {
  const navigate = useNavigate();
  const { signIn } = usePortal();
  const [step, setStep] = useState('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [challenge, setChallenge] = useState(null);
  const [method, setMethod] = useState('email');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  useLoginTheme();

  const sendEmail = async (token) => {
    setError('');
    try {
      await api.post('/auth/mfa/email', { mfa_token: token || challenge.mfa_token });
      setEmailSent(true);
    } catch (e) { setError(errMsg(e)); }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const { data } = await api.post('/auth/login', { email, password });
      if (data.token) { signIn(data.token, data.user); navigate('/portal'); return; }
      setChallenge(data);
      if (data.passkey && await canUseBiometric()) { setMethod('passkey'); setStep('mfa'); bio(data.mfa_token); return; }
      const m = data.totp_enabled ? 'totp' : 'email';
      setMethod(m);
      setStep('mfa');
      if (m === 'email') await sendEmail(data.mfa_token);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  const submitCode = async (e) => {
    e?.preventDefault();
    if (code.length !== 6) return;
    setBusy(true); setError('');
    try {
      const { data } = await api.post('/auth/mfa/verify', { mfa_token: challenge.mfa_token, method, code });
      signIn(data.token, data.user);
      navigate('/portal');
    } catch (e) { setError(errMsg(e)); setCode(''); } finally { setBusy(false); }
  };

  const bio = async (token) => {
    setBusy(true); setError('');
    try { const data = await verifyBiometric(api, '/auth/passkeys', token || challenge.mfa_token); signIn(data.token, data.user); navigate('/portal'); }
    catch (e) { setError(e?.name === 'NotAllowedError' ? 'Verifica annullata: riprova o usa il codice.' : errMsg(e)); } finally { setBusy(false); }
  };

  const switchMethod = async (m) => {
    setMethod(m); setCode(''); setError('');
    if (m === 'passkey') return bio();
    if (m === 'email' && !emailSent) await sendEmail();
  };

  return (
    <div className="min-h-screen bg-[#050505] grid lg:grid-cols-[1.1fr_1fr] portal-root" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
      <aside className="hidden lg:flex flex-col justify-between p-14 border-r border-[#1F242C] portal-grid-bg relative overflow-hidden">
        <Link to="/" className="flex items-center gap-3 text-[#A3A3A3] hover:text-white text-sm w-fit"><ArrowLeft size={16} /> Torna al sito</Link>
        <div className="portal-rise">
          <img src={LOGO} alt="BOND" className="h-14 xl:h-16 w-auto mb-10" data-testid="portal-login-logo" />
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-6">BOND · Business Information</p>
          <h1 className="font-heading text-4xl xl:text-5xl text-[#F2F2F2] leading-[1.08] mb-8" data-testid="portal-login-headline">Conosci chi hai davanti<br />e gestisci i tuoi rischi.</h1>
          <p className="text-[#8A8F97] max-w-md leading-relaxed">Report aziendali, rating, bilanci, soci e dirigenti, monitoraggio continuo e grafo delle relazioni. Tutto in un unico portale, con il tuo plafond.</p>
        </div>
        <img src="/assets/smart_info_logo.png" alt="Smart Info" className="h-8 w-auto self-start" />
      </aside>

      <main className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md portal-rise">
          <Link to="/" className="lg:hidden flex items-center gap-2 text-[#A3A3A3] text-sm mb-8"><ArrowLeft size={16} /> Torna al sito</Link>
          <img src={LOGO} alt="BOND" className="lg:hidden h-12 w-auto mb-8" data-testid="portal-login-logo-mobile" />
          <div className="mb-10">
            <ShieldCheck className="text-[#D4AF37] mb-5" size={28} />
            <h2 className="font-heading text-2xl text-[#F2F2F2] mb-2">{step === 'password' ? 'Accedi al portale' : 'Verifica in due passaggi'}</h2>
            <p className="text-sm text-[#8A8F97]">
              {step === 'password' && 'Inserisci le credenziali del tuo account BOND.'}
              {step === 'mfa' && method === 'passkey' && 'Conferma con impronta digitale, Face ID o Windows Hello.'}
              {step === 'mfa' && method === 'totp' && "Inserisci il codice a 6 cifre generato dall'app di autenticazione."}
              {step === 'mfa' && method === 'email' && `Abbiamo inviato un codice a ${challenge?.email_masked || 'la tua email'}.`}
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 border border-[#C2423A]/50 bg-[#C2423A]/10 text-[#E37A72] text-sm flex gap-3 items-start" data-testid="portal-login-error">
              <AlertCircle size={18} className="shrink-0 mt-0.5" /> {error}
            </div>
          )}

          {step === 'password' ? (
            <form onSubmit={submitPassword} className="space-y-5">
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] text-[#7A7F87] mb-2">Email</label>
                <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} placeholder="nome@azienda.it" data-testid="portal-login-email" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] text-[#7A7F87] mb-2">Password</label>
                <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} placeholder="••••••••" data-testid="portal-login-password" />
                <div className="text-right mt-2"><Link to="/forgot-password" className="text-xs text-[#D4AF37] hover:underline" data-testid="portal-forgot-link">Password dimenticata?</Link></div>
              </div>
              <Button type="submit" disabled={busy} className={`w-full h-12 ${goldBtn}`} data-testid="portal-login-submit">
                {busy ? <Loader2 className="animate-spin" size={18} /> : 'Continua'}
              </Button>
              <p className="text-sm text-[#7A7F87] text-center pt-4">Non hai un account? <Link to="/subscribe" className="text-[#D4AF37] hover:underline">Abbonati</Link></p>
            </form>
          ) : (
            <form onSubmit={submitCode} className="space-y-6">
              {challenge?.passkey && method !== 'passkey' && (
                <button type="button" onClick={() => switchMethod('passkey')} className="w-full h-11 flex items-center justify-center gap-2 text-sm border border-[#D4AF37]/60 text-[#D4AF37] rounded-sm" data-testid="mfa-method-passkey"><Fingerprint size={16} />Usa impronta o volto</button>
              )}
              {method === 'passkey' ? (
                <>
                  <Button type="button" onClick={() => bio()} disabled={busy} className={`w-full h-12 ${goldBtn}`} data-testid="mfa-passkey-submit">{busy ? <Loader2 className="animate-spin" size={18} /> : <><Fingerprint size={18} className="mr-2" />Conferma con impronta o volto</>}</Button>
                  <button type="button" onClick={() => switchMethod(challenge?.totp_enabled ? 'totp' : 'email')} className="w-full text-xs text-[#D4AF37] hover:underline" data-testid="mfa-use-code">Usa invece il codice</button>
                </>
              ) : (<>
              {challenge?.totp_enabled && (
                <div className="grid grid-cols-2 gap-2" data-testid="mfa-method-switch">
                  {[['totp', Smartphone, 'App'], ['email', Mail, 'Email']].map(([m, Icon, l]) => (
                    <button type="button" key={m} onClick={() => switchMethod(m)} data-testid={`mfa-method-${m}`}
                      className={`h-11 flex items-center justify-center gap-2 text-sm border rounded-sm transition-colors ${method === m ? 'border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5' : 'border-[#1F242C] text-[#8A8F97] hover:text-white'}`}>
                      <Icon size={16} /> {l}
                    </button>
                  ))}
                </div>
              )}
              <div className="flex justify-center"><CodeInput value={code} onChange={setCode} /></div>
              <Button type="submit" disabled={busy || code.length !== 6} className={`w-full h-12 ${goldBtn}`} data-testid="mfa-verify-submit">
                {busy ? <Loader2 className="animate-spin" size={18} /> : 'Verifica e accedi'}
              </Button>
              </>)}
              <div className="flex justify-between text-xs">
                <button type="button" onClick={() => { setStep('password'); setCode(''); setError(''); setEmailSent(false); }} className="text-[#8A8F97] hover:text-white" data-testid="mfa-back">Cambia account</button>
                {method === 'email' && <button type="button" onClick={() => sendEmail()} className="text-[#D4AF37] hover:underline" data-testid="mfa-resend-email">Invia di nuovo il codice</button>}
                {method === 'totp' && !challenge?.totp_enabled ? null : null}
              </div>
              {!challenge?.totp_enabled && (
                <p className="text-xs text-[#5A5F67] leading-relaxed">Dopo l'accesso puoi attivare l'app di autenticazione (Google Authenticator, Microsoft Authenticator…) dalla sezione Sicurezza.</p>
              )}
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
